import { useState, useEffect, useCallback } from 'react';
import { PeriodoFormState, PERIODO_FORM_INITIAL, PeriodoPayload } from '../types/periodo.types';
import { periodoService } from '../services/periodoService';
import { catalogoService } from '../services/usuarioService';
import { AñoEscolar } from '../types/usuario.types';

// ============================================================
// usePeriodoForm – creación y edición de periodos
// ============================================================

export interface PeriodoFormErrors { [field: string]: string }

export const usePeriodoForm = (idPeriodo: number | null, onSuccess: () => void) => {
  const isEditing = idPeriodo !== null;

  const [form, setForm]               = useState<PeriodoFormState>(PERIODO_FORM_INITIAL);
  const [errors, setErrors]           = useState<PeriodoFormErrors>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadingInit, setLoadingInit] = useState(true);

  // Catálogo
  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);

  // ── Cargar catálogos al montar ──────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoadingInit(true);
      try {
        const años = await catalogoService.getAñosEscolares();
        setAñosEscolares(años);
        // En modo creación, preseleccionar el año más reciente
        if (!isEditing && años.length > 0) {
          setForm(prev => ({ ...prev, anioEscolarId: String(años[0].idAnioEscolar) }));
        }
      } catch { /* silently ignore */ }
      finally { setLoadingInit(false); }
    };
    init();
  }, []);

  // ── Cargar datos en modo edición ────────────────────────────
  const loadPeriodo = useCallback(async () => {
    if (!isEditing || idPeriodo === null) return;
    setLoadingInit(true);
    try {
      const data = await periodoService.obtener(idPeriodo);
      setForm({
        anioEscolarId: String(data.anioEscolarId),
        numero:        String(data.numero),
        nombre:        data.nombre,
        fechaInicio:   data.fechaInicio,
        fechaFin:      data.fechaFin,
      });
    } catch { /* mantiene vacío */ }
    finally { setLoadingInit(false); }
  }, [idPeriodo, isEditing]);

  // ── Cambio de campo ─────────────────────────────────────────
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  // ── Validación ──────────────────────────────────────────────
  const validate = (): PeriodoFormErrors => {
    const e: PeriodoFormErrors = {};
    if (!form.anioEscolarId)         e.anioEscolarId = 'El año escolar es requerido';
    if (!form.numero.trim())         e.numero        = 'El número es requerido';
    if (form.numero && isNaN(Number(form.numero)))
                                     e.numero        = 'Debe ser un número válido';
    if (!form.nombre.trim())         e.nombre        = 'El nombre es requerido';
    if (!form.fechaInicio)           e.fechaInicio   = 'La fecha de inicio es requerida';
    if (!form.fechaFin)              e.fechaFin      = 'La fecha de fin es requerida';
    if (form.fechaInicio && form.fechaFin && form.fechaInicio >= form.fechaFin) {
      e.fechaFin = 'La fecha de fin debe ser posterior a la de inicio';
    }
    return e;
  };

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: PeriodoPayload = {
        anioEscolarId: Number(form.anioEscolarId),
        numero:        Number(form.numero),
        nombre:        form.nombre.trim(),
        fechaInicio:   form.fechaInicio,
        fechaFin:      form.fechaFin,
      };
      if (isEditing && idPeriodo) {
        await periodoService.modificar(idPeriodo, payload);
      } else {
        await periodoService.crear(payload);
      }
      onSuccess();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar el periodo');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isEditing, idPeriodo]);

  return {
    form, errors, submitting, submitError, loadingInit,
    añosEscolares,
    handleChange, handleSubmit, loadPeriodo,
  };
};
